import { type ComputedRef, reactive } from "vue";

export class PermissionSetupError extends Error {
  constructor() {
    super("Permission setup incomplete: ID required for this operation");
    this.name = "PermissionSetupError";
  }
}

type ComputedPermissionCheck = ComputedRef<boolean>;
type FunctionPermissionCheck =
  | ((...args: any[]) => boolean)
  | ((...args: any[]) => Promise<boolean>);
type PermissionCheck = ComputedPermissionCheck | FunctionPermissionCheck;

type OnlyFunctionsAllowed<T> = {
  [K in keyof T as T[K] extends FunctionPermissionCheck ? K : never]: T[K];
};

export type PermissionObject<
  N extends string,
  T extends Record<string, PermissionCheck>,
  HasId extends boolean
> = {
  can: {
    [K in N]: HasId extends true
      ? {
          [P in keyof T]: T[P] extends ComputedPermissionCheck ? boolean : T[P];
        }
      : OnlyFunctionsAllowed<T>;
  };
};

export function buildPermission<
  N extends string,
  HasId extends boolean,
  T extends Record<string, PermissionCheck>
>(name: N, _hasId: HasId, permissions: T): PermissionObject<N, T, HasId> {
  return reactive({
    can: {
      [name]: permissions,
    },
  }) as unknown as PermissionObject<N, T, HasId>;
}
